/**
 * Match report editor, who played, and what they did.
 *
 * Built for someone standing in a car park on a Saturday, not a data entry
 * clerk: three taps per player at most, and the counters only appear once a
 * player is marked as involved. Everything a scoreline already knows (the
 * result, the date) is shown, never asked for.
 *
 * "Same as last match" exists because it is the difference between a feature
 * that survives a season and one abandoned in September. Managers pick largely
 * the same team each week, so the common case should be a confirmation, not
 * twenty decisions.
 */
import { useEffect, useState } from 'react';
import { useSportConfig } from '../hooks/useSportConfig';
import ImageUploader from './ImageUploader';
import {
  getMatchReport, saveMatchReport, MAX_POTM,
  type LineupEntry, type LineupRole, type MatchReport, type MatchReportSquadMember,
} from '../api/client';
import './MatchReportModal.css';

const ROLE_LABEL: Record<LineupRole, string> = {
  started: 'Started', sub: 'Sub', unused: 'Out',
};


function blank(playerId: string, counts: readonly string[]): LineupEntry {
  const e: LineupEntry = { playerId, role: 'unused' };
  for (const k of counts) e[k] = 0;
  return e;
}

export default function MatchReportModal({
  fixtureId, onClose, onSaved,
}: {
  fixtureId: string;
  onClose: () => void;
  onSaved?: (msg: string) => void;
}) {
  const sportConfig = useSportConfig();
  // Lineup grouping follows the sport's positions; 'Utility' players fall into 'Other'.
  const POSITION_ORDER = sportConfig.positions.filter(p => p !== 'Utility');
  const reportCounts = sportConfig.stats.reportCounts;
  const keeperOnly = sportConfig.stats.keeperOnlyCounts;
  const statLabel = (k: string) => sportConfig.stats.keys.find(d => d.key === k)?.short || k;
  // The team score can only be reconciled against a per-player count when the
  // sport has one with the same name (goals, or basketball points). Rugby's
  // scoreline is points but players record tries and kicks, so no check there.
  const scoreKey = reportCounts.includes(sportConfig.score.plural) ? sportConfig.score.plural : null;
  const [report, setReport] = useState<MatchReport | null>(null);
  const [entries, setEntries] = useState<Record<string, LineupEntry>>({});
  const [gf, setGf] = useState('');
  const [ga, setGa] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isInnings = sportConfig.resultModel === 'innings';
  // Up to MAX_POTM can share it. Joint awards are normal at grassroots level.
  const [potm, setPotm] = useState<string[]>([]);
  // One photo. The consent box is NOT pre-ticked and a new photo cannot be
  // saved without it: a team photo is one object with a squad in it, and
  // per-player consent cannot act on it after the fact.
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoCaption, setPhotoCaption] = useState('');
  const [photoWasStored, setPhotoWasStored] = useState(false);
  const [photoConsent, setPhotoConsent] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await getMatchReport(fixtureId);
        if (!alive) return;
        setReport(r);
        setGf(r.goalsFor === null || r.goalsFor === undefined ? '' : String(r.goalsFor));
        setGa(r.goalsAgainst === null || r.goalsAgainst === undefined ? '' : String(r.goalsAgainst));
        const map: Record<string, LineupEntry> = {};
        for (const p of r.squad) map[p.playerId] = blank(p.playerId, reportCounts);
        for (const e of r.lineup) map[e.playerId] = { ...blank(e.playerId, reportCounts), ...e };
        setEntries(map);
        setPotm(r.potmPlayerIds || []);
        setPhotoUrl(r.photo?.url || '');
        setPhotoCaption(r.photo?.caption || '');
        setPhotoWasStored(!!r.photo?.url);
      } catch (err: any) {
        if (alive) setError(err?.message || 'Could not load the match report');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [fixtureId]);

  const setRole = (playerId: string, role: LineupRole) => {
    setEntries(prev => {
      const e: LineupEntry = { ...(prev[playerId] || blank(playerId, reportCounts)), role };
      // Dropping someone from the match clears what they supposedly did in it.
      // The server zeroes these too; doing it here as well means the numbers on
      // screen never contradict what will be saved.
      if (role === 'unused') { for (const k of reportCounts) e[k] = 0; }
      return { ...prev, [playerId]: e };
    });
    // Player of the Match has to have played: taking someone out drops their star.
    if (role === 'unused') setPotm(cur => cur.filter(id => id !== playerId));
  };

  const bump = (playerId: string, key: keyof LineupEntry, delta: number) => {
    setEntries(prev => {
      const e = { ...(prev[playerId] || blank(playerId, reportCounts)) };
      const next = Math.max(0, (Number(e[key]) || 0) + delta);
      return { ...prev, [playerId]: { ...e, [key]: next } };
    });
  };

  // Typed entry beside the steppers: basketball points, netball goals and
  // cricket runs run into the tens and hundreds, and nobody presses + 68 times.
  const setCount = (playerId: string, key: keyof LineupEntry, raw: string) => {
    setEntries(prev => {
      const e = { ...(prev[playerId] || blank(playerId, reportCounts)) };
      const n = raw.trim() === '' ? 0 : Math.max(0, Math.floor(Number(raw) || 0));
      return { ...prev, [playerId]: { ...e, [key]: n } };
    });
  };

  /** Carry the previous XI forward. Roles only, the server sends nothing
   *  else, so a hat-trick can never be inherited by whoever wore the shirt. */
  const copyPrevious = () => {
    const prev = report?.previous;
    if (!prev) return;
    // Replaces outright rather than merging: "same as last match" means the
    // last XI, not the last XI plus whatever was already ticked here.
    const next: Record<string, LineupEntry> = {};
    for (const p of report!.squad) {
      next[p.playerId] = { ...blank(p.playerId, reportCounts), role: prev.roles[p.playerId] || 'unused' };
    }
    setEntries(next);
  };

  const involved = Object.values(entries).filter(e => e.role !== 'unused');
  const goalsClaimed = scoreKey ? involved.reduce((n, e) => n + (Number(e[scoreKey]) || 0), 0) : 0;
  const scored = gf.trim() === '' ? null : Number(gf);
  // The reconciliation that makes a typo visible while it can still be fixed.
  const goalsDisagree = scoreKey !== null && scored !== null && goalsClaimed !== scored;
  const potmPrivate = (report?.squad || []).filter(p => potm.includes(p.playerId) && p.isPublic === false);
  const potmFull = potm.length >= MAX_POTM;
  const photoBlocked = !!photoUrl && !photoWasStored && !photoConsent;

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const hasScore = !isInnings && gf.trim() !== '' && ga.trim() !== '';
      const res = await saveMatchReport(
        fixtureId,
        Object.values(entries),
        hasScore ? { goalsFor: Number(gf), goalsAgainst: Number(ga) } : undefined,
        {
          potmPlayerIds: potm,
          photo: photoUrl
            ? { url: photoUrl, caption: photoCaption, consent: photoConsent || photoWasStored }
            : photoWasStored ? null : undefined,
        },
      );
      onSaved?.(
        res.statsMode === 'matches'
          ? `Report saved, season totals rebuilt for ${res.playersUpdated} players.`
          : 'Report saved. This team still uses typed season totals, so nothing was recalculated.',
      );
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Could not save the match report');
      setSaving(false);
    }
  };

  const byPosition = (squad: MatchReportSquadMember[]) => {
    const groups = new Map<string, MatchReportSquadMember[]>();
    for (const p of squad) {
      const key = POSITION_ORDER.includes(p.position || '') ? p.position! : 'Other';
      (groups.get(key) || groups.set(key, []).get(key)!).push(p);
    }
    return [...POSITION_ORDER, 'Other']
      .filter(k => groups.has(k))
      .map(k => [k, groups.get(k)!] as const);
  };

  return (
    <div className="mr-overlay" onClick={onClose}>
      <div className="mr-modal" onClick={e => e.stopPropagation()}>
        <div className="mr-head">
          <div>
            {/* Named for what the manager came to do. On a fixture that has
                not been played yet that is entering the result; afterwards it
                is correcting the report. */}
            <h2>{report && report.status !== 'played' ? 'Enter result' : 'Match report'}</h2>
            {report && <p className="mr-sub">{report.date} · {report.opponent}</p>}
          </div>
          <button className="mr-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {loading && <p className="mr-status">Loading squad…</p>}
        {error && <div className="mr-error">{error}</div>}

        {report && !loading && (
          <>
            {/* The result and who played are one event. Asking for them in two
                separate saves means the second gets forgotten. */}
            {isInnings ? (
              /* Cricket's result is innings and a margin, entered on the fixture
                 itself; the report records who played and what they did. */
              <p className="mr-hint">Result: {report.status === 'played' ? 'as entered on the fixture' : 'enter it on the fixture first'}.</p>
            ) : (
            <div className="mr-score">
              <label>
                <span>{report.homeAway === 'away' ? 'Them' : 'Us'}</span>
                <input type="number" min="0" inputMode="numeric"
                       value={report.homeAway === 'away' ? ga : gf}
                       onChange={e => (report.homeAway === 'away' ? setGa : setGf)(e.target.value)} />
              </label>
              <span className="mr-score-dash">–</span>
              <label>
                <span>{report.homeAway === 'away' ? 'Us' : 'Them'}</span>
                <input type="number" min="0" inputMode="numeric"
                       value={report.homeAway === 'away' ? gf : ga}
                       onChange={e => (report.homeAway === 'away' ? setGf : setGa)(e.target.value)} />
              </label>
              {report.status !== 'played' && (
                <span className="mr-score-note">Saving marks this played.</span>
              )}
            </div>
            )}

            {report.squad.length === 0 && (
              <p className="mr-hint">
                No players on this team yet, so there is nobody to pick. You can still
                save the score.
              </p>
            )}

            <div className="mr-summary">
              <span><strong>{involved.length}</strong> involved</span>
              {scoreKey && (
                <span className={goalsDisagree ? 'mr-warn' : ''}>
                  <strong>{goalsClaimed}</strong> of {scored ?? '-'} {sportConfig.score.plural} accounted for
                </span>
              )}
            </div>
            {potmPrivate.length > 0 && (
              <p className="mr-hint">
                {potmPrivate.length === 1
                  ? `Player of the Match won't show on your website because ${potmPrivate[0]!.firstName}'s profile isn't public.`
                  : `Player of the Match won't show on your website for ${potmPrivate.map(p => p.firstName).join(' or ')}, because their profiles aren't public.`}
              </p>
            )}

            {goalsDisagree && (
              <p className="mr-hint">
                The scoreline says {scored}. You can still save, own goals and
                unrecorded scorers are real, but it is worth a second look.
              </p>
            )}

            <div className="mr-body">
              {byPosition(report.squad).map(([group, players]) => (
                <div key={group} className="mr-group">
                  <h3>{group}</h3>
                  {players.map(p => {
                    const e = entries[p.playerId] || blank(p.playerId, reportCounts);
                    const on = e.role !== 'unused';
                    const isKeeper = sportConfig.stats.keeperPositions.includes(p.position || '');
                    const countKeys = isKeeper
                      ? [...keeperOnly, ...reportCounts.filter(k => !keeperOnly.includes(k))]
                      : reportCounts.filter(k => !keeperOnly.includes(k));
                    return (
                      <div key={p.playerId} className={`mr-row${on ? ' is-on' : ''}`}>
                        <div className="mr-who">
                          <span className="mr-num">{p.jerseyNumber ?? '-'}</span>
                          <span className="mr-name">{p.firstName} {p.lastName || ''}</span>
                        </div>
                        {on && (
                          <button type="button"
                            className={`mr-potm${potm.includes(p.playerId) ? ' is-sel' : ''}`}
                            aria-pressed={potm.includes(p.playerId)}
                            aria-label={`Player of the Match: ${p.firstName}`}
                            title={potm.includes(p.playerId) || !potmFull ? 'Player of the Match' : `You can name up to ${MAX_POTM}`}
                            disabled={!potm.includes(p.playerId) && potmFull}
                            onClick={() => setPotm(cur => cur.includes(p.playerId) ? cur.filter(id => id !== p.playerId) : [...cur, p.playerId])}>
                            ★
                          </button>
                        )}
                        <div className="mr-roles" role="group" aria-label={`Role for ${p.firstName}`}>
                          {(['started', 'sub', 'unused'] as LineupRole[]).map(r => (
                            <button key={r} type="button"
                              className={`mr-role${e.role === r ? ' is-sel' : ''}`}
                              onClick={() => setRole(p.playerId, r)}>
                              {ROLE_LABEL[r]}
                            </button>
                          ))}
                        </div>
                        {on && (
                          <div className="mr-counts">
                            {countKeys.map(key => [key, statLabel(key)] as const).map(([key, label]) => (
                              <div key={key} className="mr-count">
                                <span className="mr-count-label">{label}</span>
                                <button type="button" onClick={() => bump(p.playerId, key as any, -1)}
                                        aria-label={`One fewer ${label}`}>−</button>
                                <input type="number" inputMode="numeric" min={0} className="mr-count-val"
                                       value={(e as any)[key]} aria-label={label}
                                       onChange={ev => setCount(p.playerId, key as any, ev.target.value)}
                                       onFocus={ev => ev.target.select()} />
                                <button type="button" onClick={() => bump(p.playerId, key as any, 1)}
                                        aria-label={`One more ${label}`}>+</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            <div className="mr-photo">
              <h3>Match photo</h3>
              <ImageUploader
                label={photoUrl ? 'Replace photo' : 'Add a photo'}
                currentImage={photoUrl}
                onUpload={url => { setPhotoUrl(url); setPhotoWasStored(false); setPhotoConsent(false); }}
                onUploadingChange={setUploading}
              />
              {photoUrl && (
                <>
                  <input type="text" className="form-input" placeholder="Caption (optional)" maxLength={200}
                         value={photoCaption} onChange={e => setPhotoCaption(e.target.value)} />
                  {/* Only a NEW photo needs confirming; a stored one already
                      carries the record of who confirmed it and when. */}
                  {!photoWasStored && (
                    <label className="mr-consent">
                      <input type="checkbox" checked={photoConsent} onChange={e => setPhotoConsent(e.target.checked)} />
                      <span>
                        I have permission for everyone in this photo to appear on the club's public website,
                        including parents' permission for any children.
                      </span>
                    </label>
                  )}
                  <button type="button" className="btn btn-secondary btn-small"
                          onClick={() => { setPhotoUrl(''); setPhotoCaption(''); setPhotoConsent(false); }}>
                    Remove photo
                  </button>
                </>
              )}
            </div>

            <div className="mr-actions">
              {report.previous && (
                /* The difference between a feature that survives a season and
                   one abandoned in September: most weeks this is a
                   confirmation, not twenty fresh decisions. */
                <button type="button" className="btn btn-secondary mr-copy"
                        onClick={copyPrevious} disabled={saving}>
                  Same as {report.previous.homeAway === 'home' ? 'vs' : 'at'} {report.previous.opponent}
                </button>
              )}
              <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={save}
                      title={photoBlocked ? 'Confirm you have permission for everyone in the photo' : ''}
                      disabled={saving || uploading || photoBlocked}>
                {saving ? 'Saving…' : (report.status !== 'played' ? 'Save result' : 'Save report')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
